import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from '../components/ui/toaster';
import UltimateDashboard from '../components/UltimateDashboard';
import EnhancedEmployeeManagement from '../components/EnhancedEmployeeManagement';
import ImprovedOrgChart from '../components/ImprovedOrgChart';
import KPIDashboard from '../components/KPIDashboard';
import PayRaisesManagement from '../components/PayRaisesManagement';
import MonthlyCalculations from '../components/MonthlyCalculations';
import Configuration from '../components/Configuration';
import { LayoutDashboard, Users, Calculator, Settings, Menu, X, GitBranch, TrendingUp, BarChart3, ArrowLeft, BookOpen, HelpCircle, DollarSign, Clock, Percent, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';

// --- Guide/Instructions Component ---
const HiringPlannerGuide = () => {
  const [activeTab, setActiveTab] = useState('howto');

  const tabs = [
    { id: 'howto', name: 'How to Use' },
    { id: 'terms', name: 'Key Terms' },
    { id: 'tips', name: 'Pro Tips' },
    { id: 'faq', name: 'FAQs' },
  ];

  const howToSteps = [
    { title: 'Configure Settings First', description: 'Go to Settings to set your analysis year, base currency, and fetch current exchange rates for multi-currency support.' },
    { title: 'Add Your Employees', description: 'Navigate to Employees and add your team members with their details: name, team, role, salary, currency, and start date.' },
    { title: 'Import Existing Data (Optional)', description: 'Use the CSV import feature in Employees to bulk upload your existing headcount data.' },
    { title: 'Plan Pay Raises', description: 'Go to Pay Raises to schedule salary increases. Set effective dates and percentage/fixed increases.' },
    { title: 'View Monthly Calculations', description: 'The Calculations page shows month-by-month payroll costs, including prorated salaries for new hires.' },
    { title: 'Analyze with KPIs', description: 'Check the KPI Dashboard for headcount trends, cost per employee, and departmental breakdowns.' },
    { title: 'Visualize Your Org', description: 'The Org Chart provides a visual representation of your team structure and reporting lines.' },
  ];

  const keyTerms = [
    { name: 'Fully Loaded Cost', definition: 'Total employee cost including salary, benefits, taxes, and overhead. Typically 1.25x to 1.5x base salary.', icon: <DollarSign className="w-6 h-6 text-[#C9A64A]" /> },
    { name: 'Prorated Salary', definition: 'Partial salary for employees who start mid-year or mid-month. Calculated based on actual days/months worked.', icon: <Clock className="w-6 h-6 text-blue-400" /> },
    { name: 'Headcount', definition: 'Total number of employees. Can be measured as FTE (Full-Time Equivalent) for part-time workers.', icon: <Users className="w-6 h-6 text-purple-400" /> },
    { name: 'Burn Rate Impact', definition: 'How payroll affects your monthly cash burn. Salaries are typically 60-80% of startup expenses.', icon: <TrendingUp className="w-6 h-6 text-red-400" /> },
    { name: 'Cost Per Hire', definition: 'Recruiting costs divided by hires made. Industry average is $4,000-$5,000 per hire.', icon: <Briefcase className="w-6 h-6 text-emerald-400" /> },
    { name: 'Currency Conversion', definition: 'Converting salaries to a base currency for consolidated reporting. Uses real-time exchange rates.', icon: <Percent className="w-6 h-6 text-orange-400" /> },
  ];

  const tips = [
    { title: '💰 Plan for 1.3x Base Salary', description: 'When budgeting, multiply base salaries by 1.25-1.4x to account for benefits, taxes, equipment, and overhead.' },
    { title: '📅 Start Dates Matter', description: 'Enter accurate start dates - the tool prorates first-year costs automatically, giving you precise monthly projections.' },
    { title: '🔄 Update Exchange Rates Monthly', description: 'For international teams, refresh exchange rates in Settings monthly to maintain accurate consolidated costs.' },
    { title: '📊 Export for Board Decks', description: 'Use the CSV export feature to pull data into your financial models and board presentations.' },
    { title: '🎯 Track by Team', description: 'Assign employees to teams (Engineering, Sales, etc.) to analyze costs by department and identify optimization opportunities.' },
    { title: '📈 Model Scenarios', description: 'Create "what-if" scenarios by adding planned hires with future start dates to see impact on runway.' },
  ];

  const faqs = [
    { q: 'How are prorated salaries calculated?', a: 'For employees starting mid-year, we calculate the fraction of the year they work and multiply by annual salary. For example, starting July 1st = 6/12 = 50% of annual salary for that year.' },
    { q: 'Can I model future hires?', a: 'Yes! Add employees with future start dates. They will appear in projections starting from their start month, with costs prorated automatically.' },
    { q: 'How do I handle part-time employees?', a: 'Enter their actual annual salary (what they are paid). The tool treats all entries as the total compensation regardless of hours worked.' },
    { q: 'What about contractors?', a: 'You can add contractors as employees. Consider adding a note in their role field (e.g., "Senior Dev - Contractor") and remember contractors typically don\'t have benefits load.' },
    { q: 'How do pay raises affect projections?', a: 'Pay raises take effect from their effective date. The tool recalculates all subsequent months with the new salary, showing the increased burn rate.' },
    { q: 'Can I import from my HRIS?', a: 'Export your HRIS data to CSV with columns: Name, Team, Role, Salary, Currency, Start Date. Then use the Import CSV feature in Employees.' },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Guide & Instructions</h1>
          <p className="text-gray-400">Learn how to use the Headcount & Payroll Planner effectively</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-1 bg-white/5 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                activeTab === tab.id 
                  ? 'bg-[#C9A64A] text-[#0F1A2A]' 
                  : 'text-gray-300 hover:bg-white/10'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        {activeTab === 'howto' && (
          <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-6">How to Use This Tool</h3>
            <div className="space-y-6">
              {howToSteps.map((step, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A64A] flex items-center justify-center text-[#0F1A2A] font-bold">{idx + 1}</div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">{step.title}</h4>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'terms' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {keyTerms.map((term, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  {term.icon}
                </div>
                <h4 className="text-xl font-bold text-white mb-3">{term.name}</h4>
                <p className="text-gray-400 leading-relaxed">{term.definition}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tips.map((tip, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
                <h4 className="text-lg font-bold text-[#C9A64A] mb-3">{tip.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{tip.description}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'faq' && (
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
                <h4 className="text-lg font-bold text-white mb-3">{faq.q}</h4>
                <p className="text-gray-400 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const NavigationItem = ({ to, icon: Icon, label, isActive }) => (
  <Link to={to}>
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
        isActive
          ? 'bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] shadow-lg shadow-[#C9A64A]/50'
          : 'text-gray-300 hover:bg-[#0F1A2A] hover:text-white'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{label}</span>
    </div>
  </Link>
);

export const HiringPlannerApp = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { to: '/calculators/hiring-planner', icon: BookOpen, label: 'Guide' },
    { to: '/calculators/hiring-planner/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/calculators/hiring-planner/kpi', icon: BarChart3, label: 'KPI Dashboard' },
    { to: '/calculators/hiring-planner/employees', icon: Users, label: 'Employees' },
    { to: '/calculators/hiring-planner/org-chart', icon: GitBranch, label: 'Org Chart' },
    { to: '/calculators/hiring-planner/pay-raises', icon: TrendingUp, label: 'Pay Raises' },
    { to: '/calculators/hiring-planner/calculations', icon: Calculator, label: 'Calculations' },
    { to: '/calculators/hiring-planner/configuration', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F1A2A] to-[#1A1A1C]">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="border-[#C9A64A]/30"
        >
          {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-gradient-to-b from-[#1A1A1C] to-[#0F1A2A] border-r border-[#C9A64A]/20 w-64 transition-transform duration-300 z-40 shadow-2xl ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-6">
          <Link to="/calculators" className="flex items-center gap-2 mb-6 text-gray-400 hover:text-[#C9A64A] transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back to Calculators</span>
          </Link>
          <h1 className="text-2xl font-bold mb-8 text-white">
            Headcount <span className="text-[#C9A64A]">Planner</span>
          </h1>
          <nav className="space-y-2">
            {navItems.map((item) => (
              <NavigationItem
                key={item.to}
                {...item}
                isActive={location.pathname === item.to}
              />
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <Routes>
          <Route path="/" element={<HiringPlannerGuide />} />
          <Route path="/dashboard" element={<UltimateDashboard />} />
          <Route path="/kpi" element={<KPIDashboard />} />
          <Route path="/employees" element={<EnhancedEmployeeManagement />} />
          <Route path="/org-chart" element={<ImprovedOrgChart />} />
          <Route path="/pay-raises" element={<PayRaisesManagement />} />
          <Route path="/calculations" element={<MonthlyCalculations />} />
          <Route path="/configuration" element={<Configuration />} />
        </Routes>
      </main>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <Toaster />
    </div>
  );
};

export default HiringPlannerApp;
